import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { PWAInstallProvider } from "@/contexts/PWAInstallContext";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { SubscriptionProtectedRoute } from "@/components/layout/SubscriptionProtectedRoute";
import { OwnerOnlyRoute } from "@/components/layout/OwnerOnlyRoute";
import { MasterProtectedRoute } from "@/components/master/MasterProtectedRoute";
import { MasterLayout } from "@/components/master/MasterLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Suspense, lazy, useEffect } from "react";
import { Loader2 } from "lucide-react";

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const OAuthCallbackRecovery = () => {
  useEffect(() => {
    const reloadFromNetwork = async () => {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }

      window.location.replace(window.location.href);
    };

    reloadFromNetwork();
  }, []);

  return <PageLoader />;
};

// Lazy loaded pages - Public
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));

const Onboarding = lazy(() => import("./pages/Onboarding"));
const Install = lazy(() => import("./pages/Install"));
const Plans = lazy(() => import("./pages/Plans"));
const MySubscription = lazy(() => import("./pages/MySubscription"));
const SubscriptionExpired = lazy(() => import("./pages/SubscriptionExpired"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Marketing = lazy(() => import("./pages/Marketing"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy loaded pages - Main App
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerNew = lazy(() => import("./pages/CustomerNew"));
const CustomerEdit = lazy(() => import("./pages/CustomerEdit"));
const CustomerDetails = lazy(() => import("./pages/CustomerDetails"));
const EquipmentNew = lazy(() => import("./pages/EquipmentNew"));
const EquipmentEdit = lazy(() => import("./pages/EquipmentEdit"));
const EquipmentDetails = lazy(() => import("./pages/EquipmentDetails"));
const DefectTypes = lazy(() => import("./pages/DefectTypes"));
const PriceTable = lazy(() => import("./pages/PriceTable"));
const ServiceOrders = lazy(() => import("./pages/ServiceOrders"));
const ServiceOrderNew = lazy(() => import("./pages/ServiceOrderNew"));
const ServiceOrderEdit = lazy(() => import("./pages/ServiceOrderEdit"));
const ServiceOrderDetails = lazy(() => import("./pages/ServiceOrderDetails"));
const Products = lazy(() => import("./pages/Products"));
const ProductNew = lazy(() => import("./pages/ProductNew"));
const ProductEdit = lazy(() => import("./pages/ProductEdit"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const StockMovements = lazy(() => import("./pages/StockMovements"));
const POS = lazy(() => import("./pages/POS"));
const Sales = lazy(() => import("./pages/Sales"));
const Settings = lazy(() => import("./pages/Settings"));
const Warranties = lazy(() => import("./pages/Warranties"));
const CashRegister = lazy(() => import("./pages/CashRegister"));
const Bills = lazy(() => import("./pages/Bills"));
const FinancialReport = lazy(() => import("./pages/FinancialReport"));
const Receivables = lazy(() => import("./pages/Receivables"));
const Employees = lazy(() => import("./pages/Employees"));

// Lazy loaded pages - Suppliers
const Suppliers = lazy(() => import("./pages/Suppliers"));
const SupplierNew = lazy(() => import("./pages/SupplierNew"));
const SupplierEdit = lazy(() => import("./pages/SupplierEdit"));

// Lazy loaded pages - Support
const Support = lazy(() => import("./pages/Support"));

// Lazy loaded pages - Master Panel
const MasterDashboard = lazy(() => import("./pages/master/MasterDashboard"));
const MasterUsers = lazy(() => import("./pages/master/MasterUsers"));
const MasterUsersWithoutSubscription = lazy(() => import("./pages/master/MasterUsersWithoutSubscription"));
const MasterSubscriptions = lazy(() => import("./pages/master/MasterSubscriptions"));
const MasterPlans = lazy(() => import("./pages/master/MasterPlans"));
const MasterSupportTickets = lazy(() => import("./pages/master/MasterSupportTickets"));
const MasterCoupons = lazy(() => import("./pages/master/MasterCoupons"));
const MasterSalesReps = lazy(() => import("./pages/master/MasterSalesReps"));
const MasterTrialConversion = lazy(() => import("./pages/master/MasterTrialConversion"));
const MasterTrials = lazy(() => import("./pages/master/MasterTrials"));
const MasterPayments = lazy(() => import("./pages/master/MasterPayments"));
const MasterSyncLogs = lazy(() => import("./pages/master/MasterSyncLogs"));
const MasterCommissions = lazy(() => import("./pages/master/MasterCommissions"));
const MasterMarketingMaterials = lazy(() => import("./pages/master/MasterMarketingMaterials"));
const MasterSupportMetrics = lazy(() => import("./pages/master/MasterSupportMetrics"));

// Lazy loaded pages - Demo (public screenshots)
const DemoDashboard = lazy(() => import("./pages/demo/DemoDashboard"));
const DemoServiceOrders = lazy(() => import("./pages/demo/DemoServiceOrders"));
const DemoPOS = lazy(() => import("./pages/demo/DemoPOS"));
const DemoProducts = lazy(() => import("./pages/demo/DemoProducts"));

// Lazy loaded pages - Demo Mobile (for PWA screenshots)
const DemoDashboardMobile = lazy(() => import("./pages/demo/DemoDashboardMobile"));
const DemoServiceOrdersMobile = lazy(() => import("./pages/demo/DemoServiceOrdersMobile"));
const DemoPOSMobile = lazy(() => import("./pages/demo/DemoPOSMobile"));
const DemoProductsMobile = lazy(() => import("./pages/demo/DemoProductsMobile"));

// Optimized QueryClient with better caching defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PWAInstallProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/install" element={<Install />} />
              <Route path="/planos" element={<Plans />} />
              <Route path="/assinatura-expirada" element={<SubscriptionExpired />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/acompanhar/:id" element={<TrackOrder />} />
              <Route path="/marketing" element={<Marketing />} />
              <Route path="/~oauth/*" element={<OAuthCallbackRecovery />} />
              
              {/* Demo Routes - Public for screenshots */}
              <Route path="/demo/dashboard" element={<DemoDashboard />} />
              <Route path="/demo/ordens-servico" element={<DemoServiceOrders />} />
              <Route path="/demo/pdv" element={<DemoPOS />} />
              <Route path="/demo/produtos" element={<DemoProducts />} />
              
              {/* Demo Mobile Routes - For PWA screenshots */}
              <Route path="/demo/dashboard-mobile" element={<DemoDashboardMobile />} />
              <Route path="/demo/ordens-servico-mobile" element={<DemoServiceOrdersMobile />} />
              <Route path="/demo/pdv-mobile" element={<DemoPOSMobile />} />
              <Route path="/demo/produtos-mobile" element={<DemoProductsMobile />} />
              {/* Master Panel Routes */}
              <Route path="/master" element={<MasterProtectedRoute><MasterLayout /></MasterProtectedRoute>}>
                <Route index element={<MasterDashboard />} />
                <Route path="trial-conversion" element={<MasterTrialConversion />} />
                <Route path="trials" element={<MasterTrials />} />
                <Route path="payments" element={<MasterPayments />} />
                <Route path="sync-logs" element={<MasterSyncLogs />} />
                <Route path="users" element={<MasterUsers />} />
                <Route path="users-without-subscription" element={<MasterUsersWithoutSubscription />} />
                <Route path="subscriptions" element={<MasterSubscriptions />} />
                <Route path="plans" element={<MasterPlans />} />
                <Route path="coupons" element={<MasterCoupons />} />
                <Route path="sales-reps" element={<MasterSalesReps />} />
                <Route path="commissions" element={<MasterCommissions />} />
                <Route path="marketing-materials" element={<MasterMarketingMaterials />} />
                <Route path="support-tickets" element={<MasterSupportTickets />} />
                <Route path="support-metrics" element={<MasterSupportMetrics />} />
              </Route>
              
              {/* Protected Routes - Require subscription */}
              <Route
                path="/dashboard"
                element={
                  <SubscriptionProtectedRoute>
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                }
              />
              
              {/* Customer Routes */}
              <Route
                path="/customers"
                element={
                  <SubscriptionProtectedRoute>
                    <Customers />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/customers/new"
                element={
                  <SubscriptionProtectedRoute>
                    <CustomerNew />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <SubscriptionProtectedRoute>
                    <CustomerDetails />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/customers/:id/edit"
                element={
                  <SubscriptionProtectedRoute>
                    <CustomerEdit />
                  </SubscriptionProtectedRoute>
                }
              />
              
              {/* Equipment Routes */}
              <Route
                path="/equipment/new"
                element={
                  <SubscriptionProtectedRoute>
                    <EquipmentNew />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id"
                element={
                  <SubscriptionProtectedRoute>
                    <EquipmentDetails />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/edit"
                element={
                  <SubscriptionProtectedRoute>
                    <EquipmentEdit />
                  </SubscriptionProtectedRoute>
                }
              />
              
              {/* Service Order Routes */}
              <Route
                path="/ordens-servico"
                element={
                  <SubscriptionProtectedRoute>
                    <ServiceOrders />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/ordens-servico/novo"
                element={
                  <SubscriptionProtectedRoute>
                    <ServiceOrderNew />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/ordens-servico/:id"
                element={
                  <SubscriptionProtectedRoute>
                    <ServiceOrderDetails />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/ordens-servico/:id/editar"
                element={
                  <SubscriptionProtectedRoute>
                    <ServiceOrderEdit />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/defect-types"
                element={
                  <SubscriptionProtectedRoute>
                    <DefectTypes />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/price-table"
                element={
                  <SubscriptionProtectedRoute>
                    <PriceTable />
                  </SubscriptionProtectedRoute>
                }
              />
              {/* Supplier Routes */}
              <Route
                path="/fornecedores"
                element={
                  <SubscriptionProtectedRoute>
                    <Suppliers />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/fornecedores/novo"
                element={
                  <SubscriptionProtectedRoute>
                    <SupplierNew />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/fornecedores/:id/editar"
                element={
                  <SubscriptionProtectedRoute>
                    <SupplierEdit />
                  </SubscriptionProtectedRoute>
                }
              />
              
              {/* Product Routes */}
              <Route
                path="/products"
                element={
                  <SubscriptionProtectedRoute>
                    <Products />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/products/new"
                element={
                  <SubscriptionProtectedRoute>
                    <ProductNew />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/products/:id"
                element={
                  <SubscriptionProtectedRoute>
                    <ProductDetails />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/products/:id/edit"
                element={
                  <SubscriptionProtectedRoute>
                    <ProductEdit />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/stock-movements"
                element={
                  <SubscriptionProtectedRoute>
                    <StockMovements />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/pdv"
                element={
                  <SubscriptionProtectedRoute>
                    <POS />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/sales"
                element={
                  <SubscriptionProtectedRoute>
                    <Sales />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/vendas"
                element={
                  <SubscriptionProtectedRoute>
                    <Sales />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <SubscriptionProtectedRoute>
                    <OwnerOnlyRoute>
                      <Settings />
                    </OwnerOnlyRoute>
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/garantias"
                element={
                  <SubscriptionProtectedRoute>
                    <Warranties />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/caixa"
                element={
                  <SubscriptionProtectedRoute>
                    <CashRegister />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/contas"
                element={
                  <SubscriptionProtectedRoute>
                    <Bills />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/relatorio-financeiro"
                element={
                  <SubscriptionProtectedRoute>
                    <FinancialReport />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/receber"
                element={
                  <SubscriptionProtectedRoute>
                    <Receivables />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/minha-assinatura"
                element={
                  <SubscriptionProtectedRoute>
                    <MySubscription />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/suporte"
                element={
                  <SubscriptionProtectedRoute>
                    <Support />
                  </SubscriptionProtectedRoute>
                }
              />
              <Route
                path="/funcionarios"
                element={
                  <SubscriptionProtectedRoute>
                    <OwnerOnlyRoute>
                      <Employees />
                    </OwnerOnlyRoute>
                  </SubscriptionProtectedRoute>
                }
              />

              {/* Catch all */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
              </SubscriptionProvider>
            </AuthProvider>
          </PWAInstallProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
